import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { getSnapshot } from 'mobx-state-tree';
import * as React from 'react';
import { mg } from '../mg/mg';
import { Workarounds } from '../Workarounds';
import { OcMeasure } from './OcMeasure';

class Blackhole {
	static _value = 0

	static eat(x: any) {
		Blackhole._value += x;
	}
}

const EMPTY_INFO = {
	rowWidth: 0,
	firstVisibleRowIndex: 0,
	lastVisibleRowIndex: 0,
	nFittingRows: 0,
	nRows: 0,
};

export type IOcDatascrollInfo = typeof EMPTY_INFO

type IRowElement = {id: number, timestamp: string} | undefined;

interface IOcDatascrollProps {
	bounds: {left?: number, top?: number, width?: number, height?: number},
	rows: Array<IRowElement>,
	// state: Instance<typeof DataviewStore>, //TODO
	state: {},
	rowRenderer(o: object, width: number) : JSX.Element,
	emptyRenderer() : JSX.Element,
	minRowHeight: number,
	onChange?({rowWidth, firstVisibleRowIndex, nFittingRows} : IOcDatascrollInfo) : void,
}

interface IRowStruct {
	rendered?: {},
	value?: IRowElement,
	top: number,
	width: number,
	stateSnapshot: {},
}

@observer export class OcDatascroll extends React.Component<IOcDatascrollProps> {
	static emptyInfo() : IOcDatascrollInfo {
		return {...EMPTY_INFO};
	}

	@observable _scrollTop = 0
	@observable _scrollExtra = 0

	@computed get _nFittingRows() {
		return Math.max(1, Math.floor((this.props.bounds.height || 0) / this.props.minRowHeight));
	}

	@observable _rowWidth = 0

	@computed get _rowHeight() {
		return (this.props.bounds.height || 1) / this._nFittingRows;
	}

	@computed get _firstVisibleRowIndex() {
		const a = Math.max(0, this.props.rows.length - this._nFittingRows + 1);
		const b = Math.round(this._scrollTop / this._rowHeight);
		return Math.min(a, b);
	}

	/** Intentionally non-observable! */
	_cachedRowStructs: Array<IRowStruct> = []

	_projectRowStruct = (rowStruct: IRowStruct, index: number) => <div key={index}
		style={{width: '100%', height: this._rowHeight, position: 'absolute', overflow: 'hidden', top: rowStruct.top}}>
		{rowStruct.rendered}
	</div>

	_areSame(row1: IRowElement, row2: IRowElement) {
		if (row1 === row2) return true;
		if (typeof row1 !== 'object' || typeof row2 !== 'object') return false;
		if (!row1 || !row2) return false;
		if (row1.id !== row2.id) return false;
		if (row1.timestamp !== row2.timestamp) return false;
		if (!row1.timestamp || !row2.timestamp) return mg.shallowEqual(row1, row2);
		return true;
	}

	/** Array of structs {value, stateSnapshot, rendered, top, width}. */
	@computed get _rowStructs() {
		const {rows, state, rowRenderer} = this.props;
		const stateSnapshot = getSnapshot(state);
		//console.log('_rowStructs', mg.stringify(state.visibleColumns.map(c => c._line)), mg.stringify(state._ignoredColumnHiders))
		const n = this._nFittingRows;
		const result = this._cachedRowStructs;
		result.length = n;
		const page = Math.floor(this._firstVisibleRowIndex / n);
		const width = this._rowWidth;
		for (let i=0; i<n; ++i) {
			const elem = result[i] = (result[i] || {});
			let index = n*page + i;
			if (index < this._firstVisibleRowIndex) index += n;
			const row = index<rows.length ? rows[index] : undefined;
			const keep = this._areSame(row, elem.value) && elem.width===width && elem.stateSnapshot===stateSnapshot;
			if (!keep) elem.rendered = row ? rowRenderer(row, width) : undefined;
			elem.value = row;
			elem.top = index * this._rowHeight + this._scrollExtra;
			elem.width = width;
			elem.stateSnapshot = stateSnapshot as any; //TODO
		}
		return this._cachedRowStructs = result;
	}

	@computed get _info() : IOcDatascrollInfo {
		return {
			rowWidth: this._rowWidth,
			nFittingRows: this._nFittingRows,
			firstVisibleRowIndex: this._firstVisibleRowIndex,
			lastVisibleRowIndex: Math.min(this.props.rows.length, this._firstVisibleRowIndex+this._nFittingRows) - 1,
			nRows: this.props.rows.length,
		};
	}

	_scrollingRef?:HTMLDivElement = undefined

	_lastScrollTop = 0

	_onChange = () => this.props.onChange && this.props.onChange(this._info)

	@action _onScroll = (event: React.UIEvent) => {
		//console.log('onScroll');
		this._scrollingRef = event.nativeEvent.target as any;
		this._lastScrollTop = this._scrollTop;
		this._scrollTop = this._scrollingRef!.scrollTop;
		//console.log(this._scrollTop, this._lastScrollTop)
		mg.clearSelectionIn(this._scrollingRef);
		this._onChange();
	}

	@action _onWheel = (event: React.WheelEvent) => {
		//console.log('onWheel');
		if (!this._scrollingRef) return;
		event.preventDefault();
		const delta = Workarounds.getScrollSteps(event, 120);
		// console.log(delta, this._scrollingRef.scrollTop);
		this._scrollingRef.scrollTop += delta * this._rowHeight;
	}

	@action scrollToRow = (n: number) => {
		this._scrollTop = n * this._rowHeight;
	}

	@action _onResize = (bounds: IOcDatascrollProps['bounds']) => {
		this._cachedRowStructs = [];
		this._rowWidth = bounds.width!;
		this._onChange();
	}

	@action _onIntervall = () => {
		const oldScrollExtra = this._scrollExtra; // TODO really use it
		const current = this._scrollTop + oldScrollExtra;
		const desired = this._firstVisibleRowIndex * this._rowHeight;
		const diff = desired - current;
		const abs = Math.abs(diff);
		if (abs < 0.6) return;
		const diff2 = this._scrollTop - this._lastScrollTop;
		const dir = (abs<2 || diff2==0 ? diff : diff2) < 0 ? -1 : +1;
		const step = dir * (abs >= 2 ? 2 : 1);
		if (!this._scrollingRef) return;
		//console.log(diff, diff2, "→" + step, this._scrollingRef.scrollTop)
		this._scrollingRef.scrollTop += step;
	}

	_intervall?: number = undefined

	componentDidMount() {
		this._intervall = window.setInterval(this._onIntervall, 50);
	}

	componentDidUpdate() {
		//console.log('componentDidUpdate')
		this._onChange();
	}

	componentWillUnmount() {
		window.clearInterval(this._intervall);
	}

	render() {
		const {rows, bounds, emptyRenderer} = this.props;
		if (!bounds.width) return null;
		if (!bounds.height) return null;
		Blackhole.eat(this._firstVisibleRowIndex);

		const datascrollStyle: React.CSSProperties = {
			position: 'absolute',
			overflowX: 'hidden',
			overflowY: 'scroll',
			left: 0,
			right: 0,
			top: 0,
			bottom: 0,
		};
		const holderStyle: React.CSSProperties = {
			width: '100%',
			height: Math.max(bounds.height, this._rowHeight * rows.length),
			position: 'relative',
		};

		return <OcMeasure onResize={this._onResize}>{
			({measureRef}) => <div ref={measureRef} style={datascrollStyle} onScroll={this._onScroll} onWheel={this._onWheel}>
				<div style={{...holderStyle}}>{
					rows.length ? this._rowStructs.map(this._projectRowStruct) : emptyRenderer()
				}</div>
			</div>
		}</OcMeasure>;
	}
}
